import * as THREE from 'three';
import { setupScene } from '../sceneSetup.js';
import { setupEventListeners } from '../eventListeners.js';
import { loadModel, loadDefaultModel } from '../utils/saveLoadUtils.js';
import { vertexShader, fragmentShader } from '../shaders/projectiveDecalShaders.js';
import { atlasVertexShader, atlasFragmentShader } from '../shaders/atlasShader.js';
import { exportTextures } from '../utils/textureSaveUtils.js';
import { BrushSystem } from '../brushSystem.js';
import { BrushUI } from '../brushUI.js';
import { BrushOptimizer } from '../brushOptimizer.js';

import { initializeCopyResources, throttledUpdate, compositeTexture, updateSubmeshTextures, animate, render } from './render.js';
import { setupTexturePicker, setActiveDecal, addDecalToPicker, updateModeButtons, updateStatus, onKeyDown, onWindowResize } from './ui.js';
import { analyzeUVChannels, setupBasicMaterials, createDefaultTexture, analyzeUDIMTiles, calculateUDIMTile, createUVChannelControl, updateAllUDIMTextures, updateTextureResolution } from './uvChannels.js';
import { createUDIMAtlas, applyUDIMTextures, refreshCharacterMaterials } from './udimAtlas.js';
import { loadDecalTexture, onMouseClick, create3DProjectorAtIntersection, createMoveableDecal, createDecalPreviewObject, removeMoveableDecal, calculateSurfaceAwareRotationMatrix, updateProjectiveDecalPreview, getSafeSize, bakeProjectiveDecalToTextures, bakeProjectiveDecalToUDIM, bakeProjectiveDecalToUDIMSimple, updateAllTextures, updateProjectiveDecalMaterialUniforms, applyProjectiveDecal, createProjectiveDecalMaterial, applyProjectiveDecalToMesh, applyMoveableDecal, cancelMoveableDecal, clearDecals, updateDecalOpacity, updateDecalScale, centerModel } from './projection2d.js';
import { initializeProjective3D, setProjectionMode, create3DProjector, apply3DProjection, cancel3DProjection, set3DProjectorSize, set3DProjectorAspect, set3DProjectionOpacity, set3DProjectionFalloff } from './projection3d.js';
import { initializeBrushSystem } from './brush.js';
import { UndoManager } from '../utils/undoManager.js';

class TexturePainter {
    constructor() {
        Object.assign(this, {
            scene: null, camera: null, renderer: null, controls: null, transformControls: null, mesh: null,
            uvChannels: [], udimTiles: new Set(), decalTexture: null, currentDecal: null,
            raycaster: new THREE.Raycaster(), mouse: new THREE.Vector2(),
            textureResolution: 2048, updatePending: false, lastUpdateTime: 0, updateThrottleDelay: 1,
            projectionMode: '2d',
            projective3D: null,
            brushSystem: null,
            brushUI: null,
            brushOptimizer: null,
            atlasTexture: null,
            atlasMaterial: null,
sampleDecals: [
                { name: 'Heart', url: new URL('/img/1.png', import.meta.url).href },
                { name: 'Eggplant', url: new URL('/img/2.png', import.meta.url).href },
                { name: 'Cat', url: new URL('/img/3.png', import.meta.url).href },
                { name: 'Mandala', url: new URL('/img/4.png', import.meta.url).href   }
            ]
        });

        this.vertexShader = vertexShader;
        this.fragmentShader = fragmentShader;
        this.atlasVertexShader = atlasVertexShader;
        this.atlasFragmentShader = atlasFragmentShader;

        setupScene(this);
        setupEventListeners(this);

        this.initializeBrushSystem();
        this.initializeCopyResources();

        // Centralized undo manager (decals, brush, tattoo filter all push here)
        this.undoManager = new UndoManager(this, { maxStates: 10 });

        this.isDefaultMesh = true;
        this.udimNameMap = { 1001: 'Head', 1002: 'Upper Body', 1003: 'Lower Body', 1004: 'Ears' };

        this.loadDefaultModel();
        this.setupTexturePicker();

        this.refreshCharacterMaterials();
        
        // Set default projection mode after model is loaded
        this.ready(() => {
            this.setProjectionMode('3d');
            this.updateModeButtons('3d');
        });
    }

    getSelectedBaseSkinColor() {
        const colorPicker = document.getElementById('baseSkinColorPicker');
        if (colorPicker?.value) return colorPicker.value;
        const selector = document.getElementById('baseSkinSelector');
        return selector?.value ? selector.value : '#f4d1ae';
    }
    updateBaseSkinColor() {
        const color = this.getSelectedBaseSkinColor ? this.getSelectedBaseSkinColor() : '#f4d1ae';
        this.uvChannels.forEach((ch) => {
            if (!ch) return;
            const ctx = ch.baseContext;
            ctx.save();
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = color;
            ctx.fillRect(0, 0, ch.baseCanvas.width, ch.baseCanvas.height);
            ctx.restore();
        });
        this.uvChannels.forEach((_, i) => this.compositeTexture(i));
        this.refreshCharacterMaterials();
    }

    initializeCopyResources() { return initializeCopyResources.call(this); }
    throttledUpdate() { return throttledUpdate.call(this); }
    compositeTexture(i) { return compositeTexture.call(this, i); }
    updateSubmeshTextures(tile) { return updateSubmeshTextures.call(this, tile); }
    animate() { return animate.call(this); }
    render() { return render.call(this); }

    setupTexturePicker() { return setupTexturePicker.call(this); }
    setActiveDecal(el) { return setActiveDecal.call(this, el); }
    addDecalToPicker(url) { return addDecalToPicker.call(this, url); }
    updateModeButtons(mode) { return updateModeButtons.call(this, mode); }
    updateStatus(message) { return updateStatus.call(this, message); }
    onKeyDown(e) { return onKeyDown.call(this, e); }
    onWindowResize() { return onWindowResize.call(this); }

    analyzeUVChannels() { return analyzeUVChannels.call(this); }
    setupBasicMaterials() { return setupBasicMaterials.call(this); }
    createDefaultTexture() { return createDefaultTexture.call(this); }
    analyzeUDIMTiles(attr) { return analyzeUDIMTiles.call(this, attr); }
    calculateUDIMTile(u, v) { return calculateUDIMTile.call(this, u, v); }
    createUVChannelControl(i, tile) { return createUVChannelControl.call(this, i, tile); }
    updateAllUDIMTextures() { return updateAllUDIMTextures.call(this); }
    updateTextureResolution(res) { return updateTextureResolution.call(this, res); }

    createUDIMAtlas() { return createUDIMAtlas.call(this); }
    applyUDIMTextures() { return applyUDIMTextures.call(this); }
    refreshCharacterMaterials() { return refreshCharacterMaterials.call(this); }

    loadDecalTexture(fileOrUrl) { return loadDecalTexture.call(this, fileOrUrl); }
    onMouseClick(e) { return onMouseClick.call(this, e); }
    create3DProjectorAtIntersection(intersection) { return create3DProjectorAtIntersection.call(this, intersection); }
    createMoveableDecal(intersection) { return createMoveableDecal.call(this, intersection); }
    createDecalPreviewObject(position, normal, size) { return createDecalPreviewObject.call(this, position, normal, size); }
    removeMoveableDecal() { return removeMoveableDecal.call(this); }
    calculateSurfaceAwareRotationMatrix(intersection, quaternion) { return calculateSurfaceAwareRotationMatrix.call(this, intersection, quaternion); }
    updateProjectiveDecalPreview() { return updateProjectiveDecalPreview.call(this); }
    getSafeSize(size) { return getSafeSize.call(this, size); }
    bakeProjectiveDecalToTextures(intersection, projectorPos, projectionDirection, size, opacity, rotationMatrix = null) {
        return bakeProjectiveDecalToTextures.call(this, intersection, projectorPos, projectionDirection, size, opacity, rotationMatrix);
    }
    bakeProjectiveDecalToUDIM(channel, channelIndex, projectorPos, projectionDirection, size, opacity, rotationMatrix = null, intersection = null) {
        return bakeProjectiveDecalToUDIM.call(this, channel, channelIndex, projectorPos, projectionDirection, size, opacity, rotationMatrix, intersection);
    }
    bakeProjectiveDecalToUDIMSimple(channel, channelIndex, projectorPos, projectionDirection, size, opacity, rotationMatrix, intersection) {
        return bakeProjectiveDecalToUDIMSimple.call(this, channel, channelIndex, projectorPos, projectionDirection, size, opacity, rotationMatrix, intersection);
    }
    updateAllTextures() { return updateAllTextures.call(this); }
    updateProjectiveDecalMaterialUniforms(material, projectorPos, projectionDirection, size, opacity, rotationMatrix = null, intersectionUV = null) {
        return updateProjectiveDecalMaterialUniforms.call(this, material, projectorPos, projectionDirection, size, opacity, rotationMatrix, intersectionUV);
    }
    applyProjectiveDecal(projectorPos, targetPos, projectionDirection, size, opacity, rotationMatrix = null, intersection = null) {
        return applyProjectiveDecal.call(this, projectorPos, targetPos, projectionDirection, size, opacity, rotationMatrix, intersection);
    }
    createProjectiveDecalMaterial(projectorPos, projectionDirection, size, opacity, rotationMatrix = null, intersectionUV = null) {
        return createProjectiveDecalMaterial.call(this, projectorPos, projectionDirection, size, opacity, rotationMatrix, intersectionUV);
    }
    applyProjectiveDecalToMesh(mesh, decalMaterial, projectorPos, projectionDirection, size, intersectionUV = null) {
        return applyProjectiveDecalToMesh.call(this, mesh, decalMaterial, projectorPos, projectionDirection, size, intersectionUV);
    }
    applyMoveableDecal() { return applyMoveableDecal.call(this); }
    cancelMoveableDecal() { return cancelMoveableDecal.call(this); }
    clearDecals() { return clearDecals.call(this); }
    updateDecalOpacity(o) { return updateDecalOpacity.call(this, o); }
    updateDecalScale(s) { return updateDecalScale.call(this, s); }
    centerModel() { return centerModel.call(this); }

    async initializeProjective3D() {
        if (this._projective3DInitialized) return;
        this._projective3DInitialized = true;
        return initializeProjective3D.call(this);
    }
    async setProjectionMode(mode) { return setProjectionMode.call(this, mode); }
    create3DProjector(position, target) { return create3DProjector.call(this, position, target); }
    async apply3DProjection() { return apply3DProjection.call(this); }
    cancel3DProjection() { return cancel3DProjection.call(this); }
    set3DProjectorSize(size) { return set3DProjectorSize.call(this, size); }
    set3DProjectorAspect(aspect) { return set3DProjectorAspect.call(this, aspect); }
    set3DProjectionOpacity(opacity) { return set3DProjectionOpacity.call(this, opacity); }
    set3DProjectionFalloff(falloff) { return set3DProjectionFalloff.call(this, falloff); }

    initializeBrushSystem() { return initializeBrushSystem.call(this, { BrushSystem, BrushUI, BrushOptimizer }); }

    // Unified Undo API façade on TexturePainter, backed by UndoManager
    pushUndoCheckpoint() {
        this.undoManager?.pushCheckpoint();
    }
    undo() {
        this.undoManager?.undo();
    }

    async loadModel(file, isDefault = false, onProgress = null) {
        try {
            this.updateLoadingProgress('Starting model load...', 0);
            if (!isDefault) {
                this.isDefaultMesh = false;
                if (this.udimNameMap && this.udimNameMap[1001] === 'Head' && this.udimNameMap[1004] === 'Ears') {
                    this.udimNameMap = null;
                }
            }
            const result = await loadModel(this, file, isDefault, onProgress);
            if (this.mesh) {
                await this.initializeProjective3D();
            }
            this.updateLoadingProgress('Model loaded!', 100);
            
            // Call ready() callback if defined
            if (typeof this.ready === 'function') {
                this.ready();
            }
            
            setTimeout(() => {
                const loadingScreen = document.getElementById('loadingScreen');
                if (loadingScreen) loadingScreen.style.display = 'none';
            }, 500);
            return result;
        } catch (error) {
            console.error('Error loading model:', error);
            this.updateLoadingProgress(`Error: ${error.message}`, 0);
            throw error;
        }
    }

    updateLoadingProgress(message, progress) {
        const loadingScreen = document.getElementById('loadingScreen');
        const progressBar = document.getElementById('loadingProgress');
        const progressText = loadingScreen.querySelector('div:first-child');
        
        if (loadingScreen && progressBar && progressText) {
            progressText.textContent = message;
            progressBar.style.width = `${progress}%`;
            
            if (progress < 100) {
                loadingScreen.style.display = 'flex';
            }
        }
    }
    
    /**
     * Set a callback function to be executed when the model is fully loaded
     * @param {Function} callback - The function to call when the model is ready
     */
    ready(callback) {
        if (this.mesh && typeof callback === 'function') {
            // If model is already loaded, execute immediately
            callback();
        } else {
            // Otherwise store for execution after load
            this.ready = callback;
        }
    }
    async loadDefaultModel() {
        return this.loadModel(null, true, (progress) => {
            this.updateLoadingProgress('Loading default model...', progress);
        });
        
    }

    exportTextures(channelIndex = null) {
        return exportTextures(this, channelIndex);
    }
}

const texturePainter = new TexturePainter();
Object.defineProperty(window, 'texturePainter', { value: texturePainter, writable: false, configurable: false });
window.refreshMaterials = () => texturePainter.refreshCharacterMaterials();

export { TexturePainter, texturePainter };
